import FormInput from '@/lego/components/inputs/FormInput.vue';
import IconInput from '@/lego/components/inputs/IconInput.vue';
import CurrencyInput from '@/lego/components/inputs/numeric/CurrencyInput.vue';
import SliderInput from '@/lego/components/inputs/slider/SliderInput.vue';
import LinkableCheckboxInput from '@/lego/components/inputs/box/LinkableCheckboxInput.vue';

import { NAME_VALIDATION } from '@/regex/name';
import { SPAIN_PHONE_PREFIX_CODE } from '@/constants/phone';
import { MINAGE, MAXAGE, AVERAGE_AGE } from '@/constants/age';

export function useNameField(props) {
  return {
    component: FormInput,
    name: 'name',
    label: 'Nombre',
    placeholder: 'Nombre',
    validation: ['required', ['matches', 'min:2', NAME_VALIDATION]],
    'validation-messages': {
      matches: 'Contiene caracteres incorrectos.',
      required: 'El nombre es obligatorio.',
      min: 'Introduce un nombre de al menos 2 letras.',
    },
    ...props,
  };
}

export function useSurnameField(props) {
  return {
    component: FormInput,
    name: 'surname',
    label: 'Apellido',
    placeholder: 'Apellido',
    validation: ['required', ['matches', 'min:2', NAME_VALIDATION]],
    'validation-messages': {
      matches: 'Contiene caracteres incorrectos.',
      required: 'El apellido es obligatorio.',
      min: 'Introduce un apellido de al menos 2 letras.',
    },
    ...props,
  };
}

export function useEmailField(props) {
  return {
    component: IconInput,
    name: 'email',
    type: 'email',
    label: 'Email',
    placeholder: 'Email',
    validation: 'required|email',
    'validation-messages': {
      required: 'El email es obligatorio.',
      email: 'El formato no es correcto.',
    },
    icon: 'input-email',
    ...props,
  };
}

export function usePhoneField(props) {
  return {
    component: FormInput,
    name: 'phone',
    type: 'phone',
    noExample: true,
    showCodeOnList: true,
    noFlags: true,
    borderRadius: 12,
    defaultCountryCode: SPAIN_PHONE_PREFIX_CODE,
    label: 'Teléfono',
    translations: {
      phoneNumberLabel: 'Introduce tu teléfono',
    },
    validation: 'required|phoneIsValid',
    'validation-messages': {
      required: 'El teléfono es obligatorio.',
      phoneIsValid: 'El número introducido es inválido.',
    },
    ...props,
  };
}

export function useSelectField(props) {
  return {
    component: FormInput,
    name: 'select',
    type: 'triotecaSelect',
    label: 'Selecciona una opción',
    placeholder: 'Selecciona una opción',
    validation: 'required',
    'validation-messages': {
      required: 'El campo es obligatorio.',
    },
    ...props,
  };
}

export function useTermsField(props) {
  return {
    component: LinkableCheckboxInput,
    name: 'agreement',
    label: [
      {
        label: 'He leído y acepto la',
        link: 'https://trioteca.com/legal/politica-de-privacidad',
        text: 'Política de Privacidad.',
      },
    ],
    labelPosition: 'after',
    validation: 'accepted',
    'validation-messages': {
      accepted: 'El campo es obligatorio.',
    },
    ...props,
  };
}

export function useLinkableCheckboxField(props) {
  return {
    component: LinkableCheckboxInput,
    labelPosition: 'after',
    validation: 'accepted',
    'validation-messages': {
      accepted: 'El campo es obligatorio.',
    },
    ...props,
  };
}

export function useNewsletterField(props) {
  return {
    component: FormInput,
    type: 'checkbox',
    name: 'isNewsletterSubscriber',
    label: 'Acepto recibir comunicaciones con fines comerciales que puedan ser mi interés.',
    ...props,
  };
}

export function useCheckboxField(props) {
  return {
    component: FormInput,
    name: 'check',
    type: 'checkbox',
    'validation-messages': {
      accepted: 'El campo es obligatorio.',
    },
    ...props,
  };
}

export function useRadioField(props) {
  return {
    component: FormInput,
    name: 'radio',
    type: 'radio',
    'validation-messages': {
      required: 'El campo es obligatorio.',
    },
    ...props,
  };
}

export function useHiddenRadioField(props) {
  return {
    component: FormInput,
    name: 'radio',
    type: 'radio',
    hiddenInput: true,
    'validation-messages': {
      required: 'El campo es obligatorio.',
    },
    ...props,
  };
}

export function useCurrencyField(props) {
  return {
    component: CurrencyInput,
    name: 'currency',
    validation: 'required',
    'validation-messages': {
      required: 'El campo es obligatorio.',
    },
    ...props,
  };
}

export function usePriceField(props) {
  return {
    component: CurrencyInput,
    id: 'price',
    name: 'price',
    min: 35000,
    max: 10000000,
    validation: 'bail|required|minimum:35000,price|maximum:10000000,price',
    'validation-messages': {
      required: 'El campo es obligatorio.',
      minimum: 'El valor debería ser, al menos, 35.000€.',
      maximum: 'El valor debe ser de 10.000.000€ o menos.',
    },
    ...props,
  };
}

export function useSliderField(props) {
  return {
    component: SliderInput,
    id: 'slider',
    name: 'slider',
    validation: 'required',
    'validation-messages': {
      required: 'El campo es obligatorio.',
    },
    ...props,
  };
}

export function useDateInputField(props) {
  return {
    component: FormInput,
    name: 'date',
    type: 'date',
    validation: 'required',
    'validation-messages': {
      required: 'El campo es obligatorio.',
    },
    ...props,
  };
}

export function useBirthDateInputField(props) {
  return {
    component: FormInput,
    name: 'birthDate',
    type: 'date',
    label: 'Fecha de nacimiento',
    labelSize: 'sm',
    placeholder: 'dd/mm/aaaa',
    'open-date': AVERAGE_AGE,
    'initial-view': 'year',
    validation: 'bail|required|after:' + 
      new Date(new Date().setFullYear(new Date().getFullYear() - MAXAGE)).toISOString().split('T')[0] + 
      '|before:' + new Date(new Date().setFullYear(new Date().getFullYear() - MINAGE)).toISOString().split('T')[0],
    'validation-messages': {
      required: 'La fecha de nacimiento es obligatoria.',
      after: 'La edad no debe superar los ' + MAXAGE + ' años.',
      before: 'La edad no debe ser inferior a ' + MINAGE + ' años.',
    },
    ...props,
  };
}

export function useDNIExpirationField(props) {
  return {
    component: FormInput,
    name: 'dniExpirationDate',
    type: 'date',
    label: 'Fecha de validez',
    labelSize: 'sm',
    placeholder: 'dd/mm/aaaa',
    'initial-view': 'year',
    validation: 'bail|required|after:' + new Date().toISOString().split('T')[0],
    'validation-messages': {
      required: 'La fecha de validez es obligatoria.',
      after: 'La fecha de validez no puede ser anterior a hoy.',
    },
    ...props,
  };
}

export function useDNIField(props) {
  return {
    component: FormInput,
    name: 'dni',
    type: 'text',
    label: 'Número de DNI',
    labelSize: 'sm',
    placeholder: '00000000X',
    validation: 'bail|required|validDNI',
    'validation-messages': {
      required: 'El DNI es obligatorio.',
      validDNI: 'El DNI introducido no es válido.',
    },
    ...props,
  };
}

export function useNIEField(props) {
  return {
    component: FormInput,
    name: 'nie',
    type: 'text',
    label: 'Número de NIE',
    labelSize: 'sm',
    placeholder: '00000000X',
    validation: 'bail|required|validNIE',
    'validation-messages': {
      required: 'El NIE es obligatorio.',
      validNIE: 'El NIE introducido no es válido.',
    },
    ...props,
  };
}

export function useSupportNumberField(props) {
  return {
    component: FormInput,
    name: 'nieSupportNumber',
    type: 'text',
    label: 'Número de soporte',
    labelSize: 'sm',
    placeholder: '00000000X',
    validation: 'bail|required|validSupportNumber',
    'validation-messages': {
      required: 'El número de soporte es obligatorio.',
      validSupportNumber: 'El número de soporte introducido no es válido.',
    },
    ...props,
  };
}

export function usePostalCodeField(props) {
  return {
    component: FormInput,
    name: 'postalCode',
    type: 'text',
    label: 'Código postal',
    placeholder: '28000',
    validation: 'bail|required|max:5,length',
    'validation-messages': {
      required: 'El código postal es obligatorio.',
      max: 'El código postal no puede superar los 5 dígitos.',
    },
    ...props,
  };
}